import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

/* SCAPI ShopperProducts.getProducts accepts at most 24 ids per call. */
const PRODUCTS_PER_REQUEST = 24

const chunkArray = (items, size) => {
    const chunks = []
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size))
    }
    return chunks
}

/**
 * Resolves Vicky's ordered For You ids against SFCC, the same way smart search does
 * (see utils/smart-search.js → buildSmartSearchResults):
 *
 *   1. Fetch every id chunk from ShopperProducts in parallel (24 ids per call).
 *   2. Drop ids that do not resolve to a product, or whose live stock is zero.
 *   3. Keep Vicky's ranking order.
 *
 * This gives the surface an exact "how many products can actually render" count, so
 * pagination and the item counter are honest instead of echoing the number of ids
 * Vicky returned.
 *
 * Uses the CommerceApiProvider's pre-configured `shopperProducts` client so the call
 * goes through the same-origin SCAPI proxy (`/mobify/proxy/api`) as every other
 * shopper query — a hand-built client would hit the SCAPI host directly and be
 * blocked by the Content-Security-Policy.
 *
 * @param {string[]} ids - Vicky's ordered product ids.
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - Gate the request.
 * @returns {{ids: string[], total: number, isLoading: boolean}}
 *   `ids` are the resolvable ids in Vicky's order; `total` is their count.
 */
export const useFypRecommendationPool = (ids, {enabled = true} = {}) => {
    const {getTokenWhenReady} = useAccessToken() || {}
    const {shopperProducts} = useCommerceApi() || {}
    const {site, locale} = useMultiSite() || {}
    const siteId = site?.id
    const localeId = typeof locale === 'string' ? locale : locale?.id

    const orderedIds = useMemo(
        () => (Array.isArray(ids) ? ids.filter((id) => Boolean(id)) : []),
        [ids]
    )

    const canRun =
        enabled &&
        orderedIds.length > 0 &&
        Boolean(shopperProducts) &&
        typeof shopperProducts?.getProducts === 'function' &&
        Boolean(getTokenWhenReady)

    const {data, isLoading} = useQuery({
        queryKey: ['fyp', 'recommendationPool', siteId, localeId, orderedIds.join(',')],
        enabled: canRun,
        queryFn: async () => {
            const token = await getTokenWhenReady()
            const chunks = chunkArray(orderedIds, PRODUCTS_PER_REQUEST)

            const responses = await Promise.all(
                chunks.map((chunk) =>
                    shopperProducts
                        .getProducts({
                            headers: {Authorization: `Bearer ${token}`},
                            parameters: {
                                ids: chunk.join(','),
                                expand: ['availability'],
                                ...(localeId ? {locale: localeId} : {})
                            }
                        })
                        .then((result) => result?.data || [])
                        .catch(() => [])
                )
            )

            const productById = new Map(
                responses
                    .flat()
                    .filter((product) => product?.id)
                    .map((product) => [product.id, product])
            )

            return orderedIds.filter((id) => {
                const product = productById.get(id)
                if (!product) return false
                const stockLevel = product.inventory?.stockLevel
                return stockLevel == null ? true : stockLevel > 0
            })
        },
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false
    })

    const resolvedIds = data || []

    return {
        ids: resolvedIds,
        total: resolvedIds.length,
        isLoading: canRun && isLoading && !data
    }
}
